import { ref, onBeforeUnmount } from 'vue'

// Wraps the requestId-counter + AbortController + ERR_CANCELED-filtering +
// unmount-abort dance that every report screen's load() used to hand-roll.
// `run(fetchFn, onSuccess, onError, onSettled)` aborts any in-flight call,
// invokes `fetchFn(signal)`, and only applies `onSuccess`/`onError`/`onSettled`
// if no newer `run()` call has superseded this one by the time it settles.
// `onSettled` always runs last (success or failure) for callers that need to
// react to completion beyond the built-in `loading` flag, e.g. a screen with
// its own separate "loading more" state alongside the primary load.
//
// `initialLoading` defaults to true since most callers fetch immediately on
// mount and want the loading state to hold from first render, not just from
// the first `run()` call.
export function useCancelableLoad({ initialLoading = true } = {}) {
    const loading = ref(initialLoading)
    const error = ref(null)

    let requestId = 0
    let abortController = null

    function run(fetchFn, onSuccess, onError, onSettled) {
        const id = ++requestId

        abortController?.abort()
        abortController = new AbortController()

        loading.value = true
        error.value = null

        // fetchFn is invoked synchronously (so signal listeners it attaches
        // are guaranteed to be in place before any abort can occur), but a
        // synchronous throw is still funneled into the promise chain below
        // rather than escaping run() and leaving `loading` stuck at true.
        let result
        try {
            result = fetchFn(abortController.signal)
        } catch (err) {
            result = Promise.reject(err)
        }

        return Promise.resolve(result)
            .then((response) => {
                if (id !== requestId) return
                onSuccess?.(response)
            })
            .catch((err) => {
                if (id !== requestId) return
                if (typeof err === 'object' && err?.code === 'ERR_CANCELED')
                    return

                error.value = err
                onError?.(err)
            })
            .finally(() => {
                if (id === requestId) {
                    loading.value = false
                    onSettled?.()
                }
            })
    }

    onBeforeUnmount(() => {
        abortController?.abort()
    })

    return { loading, error, run }
}
